import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import LoginPage from './pages/LoginPage';
import MainLayout from './components/MainLayout';
import HomePage from './pages/HomePage';
import TasksPage from './pages/TasksPage';
import ProductionPage from './pages/ProductionPage';
import MaintenancePage from './pages/MaintenancePage';
import OwningCostPage from './pages/OwningCostPage';
import DieselConsumptionPage from './pages/DieselConsumptionPage';
import DieselDeletionLogPage from './pages/DieselDeletionLogPage';
import InventoryManagement from './pages/InventoryManagement';
import PartsReleasePage from './pages/PartsReleasePage';
import PurchaseOrdersPage from './pages/PurchaseOrdersPage';
import IncompletePOPage from './pages/IncompletePOPage';
import IndentSummaryPage from './pages/IndentSummaryPage';
import PurchaseHistoryPage from './pages/PurchaseHistoryPage';
import QuoteAnalysisPage from './pages/QuoteAnalysisPage';
import ApprovalRightsPage from './pages/ApprovalRightsPage';
import PriceAnalyticsPage from './pages/PriceAnalyticsPage';
import AssetManagement from './pages/AssetManagement';
import TyreManagementPage from './pages/TyreManagementPage';
import ScrapPage from './pages/ScrapPage';
import UserManagementPage from './pages/UserManagementPage';
import RoleManagementPage from './pages/RoleManagementPage';
import SettingsPage from './pages/SettingsPage';
import MigratePage from './pages/MigratePage';
import Analytics from './pages/Analytics';
import ActivityLog from './pages/ActivityLog';
import DeletionAuditLogPage from './pages/DeletionAuditLogPage';
import ReportsPage from './pages/ReportsPage';
import ArchivedScansPage from './pages/ArchivedScansPage';
import CannibalizationPage from './pages/CannibalizationPage';
import PartsDirectoryPage from './pages/PartsDirectoryPage';
import MonthlyReviewPage from './pages/MonthlyReviewPage';
import QuickLogPage from './pages/QuickLogPage';
import StoresQueuePage from './pages/StoresQueuePage';
import OfflineBanner from './components/OfflineBanner';
import { initOfflineSync } from './utils/offlineSync';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Boot offline sync listener — auto-syncs pending entries when online
  useEffect(() => {
    const cleanup = initOfflineSync();
    return cleanup;
  }, []);

  // Refresh user permissions from server on every app load
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(freshUser => {
        if (freshUser) {
          localStorage.setItem('user', JSON.stringify(freshUser));
          setUser(freshUser);
        }
      })
      .catch(() => {}); // silent — offline or token expired
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('original_token');
    localStorage.removeItem('impersonating');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        {!user ? (
          <>
            <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </>
        ) : (
          <Route element={<MainLayout user={user} onLogout={handleLogout} />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/tasks" element={<TasksPage user={user} />} />
            <Route path="/production" element={<ProductionPage user={user} />} />
            {/* Maintenance routes with section parameter */}
            <Route path="/maintenance" element={<Navigate to="/maintenance/truck" replace />} />
            <Route path="/maintenance/:section" element={<MaintenancePage user={user} />} />
            <Route path="/maintenance/owning-cost" element={<OwningCostPage user={user} />} />
            <Route path="/diesel" element={<DieselConsumptionPage user={user} />} />
            <Route path="/diesel/deletion-log" element={<DieselDeletionLogPage user={user} />} />
            {/* Store & Inventory routes */}
            <Route path="/inventory" element={<InventoryManagement user={user} />} />
            <Route path="/inventory/parts-release" element={<Navigate to="/inventory/parts-release/new" replace />} />
            <Route path="/inventory/parts-release/new" element={<PartsReleasePage user={user} />} />
            <Route path="/inventory/parts-release/pending" element={<PartsReleasePage user={user} />} />
            <Route path="/inventory/parts-release/completed" element={<PartsReleasePage user={user} />} />
            <Route path="/inventory/purchase-orders" element={<Navigate to="/inventory/purchase-orders/indent" replace />} />
            <Route path="/inventory/purchase-orders/new" element={<Navigate to="/inventory/purchase-orders/indent" replace />} />
            <Route path="/inventory/purchase-orders/indent" element={<PurchaseOrdersPage user={user} />} />
            <Route path="/inventory/purchase-orders/quotations" element={<Navigate to="/inventory/quotations/received" replace />} />
            <Route path="/inventory/purchase-orders/pending" element={<PurchaseOrdersPage user={user} />} />
            <Route path="/inventory/purchase-orders/completed" element={<PurchaseOrdersPage user={user} />} />
            <Route path="/inventory/purchase-orders/history" element={<PurchaseOrdersPage user={user} />} />
            <Route path="/inventory/incomplete-pos" element={<Navigate to="/inventory/purchase-orders/pending" replace />} />
            <Route path="/inventory/history" element={<Navigate to="/inventory/purchase-orders/history" replace />} />
            <Route path="/inventory/history" element={<PurchaseHistoryPage user={user} />} />
            <Route path="/inventory/indent-summary" element={<IndentSummaryPage user={user} />} />
            <Route path="/inventory/quotations" element={<Navigate to="/inventory/quotations/received" replace />} />
            <Route path="/inventory/quotations/received" element={<PurchaseOrdersPage user={user} />} />
            <Route path="/inventory/quotations/analysis" element={<QuoteAnalysisPage user={user} />} />
            <Route path="/inventory/quote-analysis" element={<Navigate to="/inventory/quotations/analysis" replace />} />
            <Route path="/assets" element={<AssetManagement user={user} />} />
            <Route path="/tyres" element={<TyreManagementPage user={user} />} />
            <Route path="/scrap" element={<ScrapPage user={user} />} />
            <Route path="/analytics" element={<Analytics user={user} />} />
            <Route path="/inventory/price-analytics" element={<PriceAnalyticsPage user={user} />} />
            {/* User Management: accessible to admins and dept heads with can_manage_users */}
            {(user.is_admin || user.is_super_admin || user.permissions?.can_manage_users) && (
              <Route path="/users" element={<UserManagementPage user={user} />} />
            )}
            {user.is_admin && (
              <>
                <Route path="/roles" element={<RoleManagementPage user={user} />} />
                <Route path="/approval-rights" element={<ApprovalRightsPage user={user} />} />
                <Route path="/settings" element={<SettingsPage user={user} />} />
                <Route path="/activity-log" element={<ActivityLog user={user} />} />
              </>
            )}
            {(user?.is_admin || user?.is_super_admin) && (
              <Route path="/reports" element={<ReportsPage user={user} />} />
            )}
            {/* Deletion Audit Log — restricted to Nitin & Vishnu only */}
            {['nitinj@lsnminerals.com', 'vishnu@lsnminerals.com'].includes(user.email) && (
              <Route path="/admin/deletion-audit-log" element={<DeletionAuditLogPage user={user} />} />
            )}
            {/* One-time migration page — super admin only */}
            {user.is_super_admin && (
              <Route path="/migrate" element={<MigratePage />} />
            )}
            {/* Scanned Cards Archive — super admin only */}
            {user.is_super_admin && (
              <Route path="/archived-scans" element={<ArchivedScansPage user={user} />} />
            )}
            <Route path="/cannibalization" element={<CannibalizationPage user={user} />} />
            <Route path="/inventory/parts-directory" element={<PartsDirectoryPage user={user} />} />
            <Route path="/quick-log" element={<QuickLogPage user={user} />} />
            <Route path="/stores-queue" element={<StoresQueuePage user={user} />} />
            {(user?.is_admin || user?.is_super_admin) && (
              <Route path="/monthly-review" element={<MonthlyReviewPage user={user} />} />
            )}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        )}
      </Routes>
      <Toaster position="top-right" richColors />
      <OfflineBanner />
    </BrowserRouter>
  );
}

export default App;
